import { useMsal } from "@azure/msal-react";
import { addLocale } from "primereact/api";
import { useEffect, useState } from "react";
import { Button, Modal, Tooltip, OverlayTrigger } from "react-bootstrap";
import {
    FaExclamationTriangle,
    FaInfoCircle,
    FaArrowRight,
} from "react-icons/fa";
import styled from "styled-components";

import { ptBrDatepickerTranslations } from "../../i18n/locales/pt-br";
import GenericUtils from "../../utils/GenericUtils";
import { ReactComponent as InformationIcon } from "../../assets/icons/Information.svg";
import { WarningHighLight, RightArrowOutlineIcon } from "../Icons";
import { MyPanelModal } from "../../pages/MeuPainel/modal";

const Container = styled.div`
    padding: 24px;
    background: #f0f2f2;
    gap: 40px;
    border-radius: 24px;
`;

const Title = styled.h2`
    font-family: "Poppins";
    font-weight: 500;
    font-size: 24px;
    line-height: 140%;
    letter-spacing: 0px;
    vertical-align: middle;
    color: #585858;
    margin-bottom: 24px;
    margin-top: 32px;
`;

const SectionTitle = styled.h3`
    color: #8b0000;
    height: 28px;
    font-family: "Poppins";
    font-weight: 500;
    font-size: 20px;
    line-height: 140%;
    letter-spacing: 0px;
    vertical-align: middle;
    margin: 0px;
`;

const AlertBox = styled.div`
    width: 60%;
    background-color: #f4ecff;
    color: #4b0082;
    padding: 16px;
    border-radius: 16px;
    margin: 16px 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 16px;
    @media (max-width: 768px) {
        width: 100%;
        padding: 0px;
    }
`;

const InfoIcon = styled(FaInfoCircle)`
    width: 20px;
    height: 20px;
    margin: 4px;
    color: #5e17cf;

    @media (max-width: 768px) {
        width: 50px;
        height: 50px;
    }
`;

const Attention = styled.p`
    color: #863bff;
    font-size: 14px;
    margin: 0 0 32px 0;
    font-family: "Source Sans Pro";
    font-weight: 600;
    font-size: 15px;
    line-height: 156%;
    letter-spacing: 0px;
`;

const InputContainer = styled.div`
    display: flex;
    // gap: 20px;
    margin-top: 20px;
    flex-wrap: wrap;
    justify-content: space-between;
`;

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Label = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
    font-family: "Source Sans Pro";
    font-weight: 600;
    font-size: 17px;
    line-height: 150%;
    letter-spacing: 0px;
`;

const InputField = styled.div`
    display: flex;
    align-items: center;
    padding: 0px;
    width: 200px;
    height: 66px;
    border-radius: 18px;
    border: 1px solid #d48806;
    box-shadow: 0px 4px 24px 0px #0000001f;
    background: #ffffff;
`;

const InputText = styled.div`
    border: none;
    width: 20%;
    font-family: "Poppins";
    font-weight: 500;
    font-size: 24px;
    line-height: 140%;
    letter-spacing: 0px;
    text-align: center;
    margin: 7px 16px;
`;

const IconInfo = styled(InformationIcon)`
    margin: 6px;
    width: 18px;
    height: 18px;
    color: #404040;
    cursor: pointer;
`;

const IconAlert = styled(WarningHighLight)`
    margin: 2px;
    width: 18px;
    height: 18px;
`;

const CustomTooltip = styled(Tooltip)`
    --bs-tooltip-opacity: 1 !important;

    .tooltip-inner {
        max-width: 441px;
        background: #ffffff !important;
        color: #404040;
        font-family: "Source Sans Pro", sans-serif;
        font-weight: 600;
        font-size: 15px;
        line-height: 156%;
        letter-spacing: 0px;
        box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.12);
        border-radius: 8px;
        z-index: 8000;
        opacity: 1 !important;
    }
    .tooltip-arrow::before {
        border-top-color: #ffffff !important;
        opacity: 1 !important;
    }
`;

addLocale("pt-BR", ptBrDatepickerTranslations);

export interface DadosPendentesProps {
    pendingData: {
        pendenciasAgenda: {
            alocacao: {
                quantidade: number;
            };
            checkin: {
                quantidade: number;
                cartoesVencidos: any[];
            };
            antecipacaoSemVinculo: {
                quantidade: number;
                cartoesAntecipacao: any[];
            };
        };
    } | null;
}

const DadosPendentes = ({ pendingData }: DadosPendentesProps) => {
    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenTemplateManager();
    const [isLoading, setIsLoading] = useState(true);
    type CardId = "alocacao" | "check-pendentes" | "antecipacao-sem-vinculo";
    const CARD_ID_ALOCACAO = "alocacao" as CardId;
    const CARD_ID_CHECK_PENDENTES = "check-pendentes" as CardId;
    const CARD_ID_ANTECIPACAO_SEM_VINCULO = "antecipacao-sem-vinculo" as CardId;

    interface Card {
        id: CardId;
        label: string;
        tooltip: string | React.ReactNode;
    }
    const [showModal, setShowModal] = useState(false);
    const [showDialogPendingCheckin, setShowDialogPendingCheckin] =
        useState(false);
    const [showDialogCartaoSemVinculo, setShowDialogCartaoSemVinculo] =
        useState(false);
    const handleCardClick = (id: CardId) => {
        if (id === "alocacao") {
            window.location.href = "/minha-agenda";
        } else if (id === CARD_ID_CHECK_PENDENTES) {
            setShowDialogPendingCheckin(true);
        } else {
            setShowDialogCartaoSemVinculo(true);
        }
    };
    const handleClose = () => setShowModal(false);
    const renderTooltip = (msg: string | React.ReactNode, id: string) => (
        <CustomTooltip id={`tooltip-${id}`}>{msg}</CustomTooltip>
    );

    const cards: Card[] = [
        {
            id: "alocacao",
            label: "Cartões pendentes de alocação",
            tooltip:
                'Cartão que não foi alocado na agenda e ainda permanece no painel “Atividades para alocação".',
        },
        {
            id: CARD_ID_CHECK_PENDENTES,
            label: "Cartões pendentes de check-in",
            tooltip: (
                <>
                    <p>Cartão alocado no passado com check-in não realizado.</p>
                    <p>
                        <strong>&quot;Não realizado&quot;</strong>: pode ser
                        justificado por Esquecimento ou Ausência ou Antecipação.
                    </p>
                    <p>
                        <strong>&quot;Pendência de justificativa&quot;</strong>:
                        para regularização, entre em contato com a sua
                        coordenação ou com a área de Medicina do Trabalho.
                    </p>
                </>
            ),
        },
        {
            id: CARD_ID_ANTECIPACAO_SEM_VINCULO,
            label: "Cartões de antecipação sem vínculo",
            tooltip:
                "Cartão criado para antecipação de atividade, mas ainda não vinculado a atividade antecipada.",
        },
    ];
    const getQuantidadeByCardId = (id: CardId): number => {
        switch (id) {
            case "alocacao":
                return pendingData?.pendenciasAgenda?.alocacao?.quantidade ?? 0;
            case CARD_ID_CHECK_PENDENTES:
                return pendingData?.pendenciasAgenda?.checkin?.quantidade ?? 0;
            case "antecipacao-sem-vinculo":
                return (
                    pendingData?.pendenciasAgenda?.antecipacaoSemVinculo
                        ?.quantidade ?? 0
                );
            default:
                return 0;
        }
    };

    // console.log(pendingData?.pendenciasAgenda.checkin.cartoesVencidos);

    return (
        <>
            <Title>Dados pendentes para ação</Title>
            <Container>
                <SectionTitle>Minha Agenda</SectionTitle>
                <AlertBox>
                    <InfoIcon />
                    <span
                        style={{
                            fontFamily: "Source Sans Pro",
                            fontWeight: 600,
                            fontSize: "17px",
                            lineHeight: "150%",
                            letterSpacing: "0px",
                            color: "#000000",
                        }}
                    >
                        Verifique se existem pendências que requerem sua ação
                        para regularização.
                    </span>
                </AlertBox>
                <Attention>
                    Atenção! Cartões não alocados e pendentes de check-in podem
                    impactar no resultado do fechamento mensal.
                </Attention>

                <InputContainer>
                    {cards.map((item) => {
                        const quantidade = getQuantidadeByCardId(item.id);
                        const isDisabled = quantidade === 0;

                        return (
                            <InputWrapper key={item.id}>
                                <Label>
                                    {item.label}
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={renderTooltip(
                                            item.tooltip,
                                            item.id
                                        )}
                                    >
                                        <IconInfo />
                                    </OverlayTrigger>
                                </Label>
                                <InputField
                                    style={{
                                        opacity: isDisabled ? 0.5 : 1,
                                        cursor: isDisabled
                                            ? "not-allowed"
                                            : "pointer",
                                        pointerEvents: "auto",
                                    }}
                                    onClick={() =>
                                        !isDisabled && handleCardClick(item.id)
                                    }
                                >
                                    <InputText>
                                        {quantidade === 0
                                            ? quantidade
                                            : quantidade
                                                  .toString()
                                                  .padStart(2, "0")}
                                    </InputText>
                                    <IconAlert
                                        style={{
                                            visibility: isDisabled
                                                ? "hidden"
                                                : "visible",
                                        }}
                                    />
                                    <RightArrowOutlineIcon
                                        style={{
                                            width: "16px",
                                            height: "16px",
                                            marginTop: "4px",
                                            marginBottom: "4px",
                                            marginLeft: "auto",
                                            marginRight: "16px",
                                        }}
                                    />
                                </InputField>
                            </InputWrapper>
                        );
                    })}
                </InputContainer>
                {pendingData &&
                    pendingData?.pendenciasAgenda?.checkin?.cartoesVencidos
                        ?.length > 0 && (
                        <MyPanelModal
                            open={showDialogPendingCheckin}
                            onClose={() => setShowDialogPendingCheckin(false)}
                            title="Atividades pendentes de check-in"
                            subtitle="Confira aqui as atividades que estão com pendências de check-in ou não justificadas"
                            warning="Atenção! Cartão pendente de check-in ou não justificado pode impactar no resultado do fechamento mensal."
                            columns={[
                                "Atividade",
                                "Curso",
                                "Tipo de Atividade",
                                "CH (hora/aula)",
                                "Data de alocação",
                                "Período de referência",
                                "Status",
                                "Ação do Docente",
                            ]}
                            rows={
                                pendingData.pendenciasAgenda.checkin
                                    .cartoesVencidos
                            }
                        />
                    )}

                {pendingData &&
                    pendingData?.pendenciasAgenda?.antecipacaoSemVinculo
                        ?.cartoesAntecipacao?.length > 0 && (
                        <MyPanelModal
                            open={showDialogCartaoSemVinculo}
                            onClose={() => setShowDialogCartaoSemVinculo(false)}
                            title="Antecipação sem vínculo"
                            subtitle="Confira aqui as atividades que foram antecipadas e não foram vinculadas à atividade original."
                            warning="Atenção! Cartão de antecipação somente pode ser vinculado a atividade antecipada com status Não Realizado representado pelo ícone"
                            columns={[
                                "Atividade",
                                "Curso",
                                "Tipo de Atividade",
                                "CH (hora/aula)",
                                "Data de alocação",
                                "Ação do Docente",
                            ]}
                            rows={
                                pendingData?.pendenciasAgenda
                                    .antecipacaoSemVinculo.cartoesAntecipacao
                            }
                        />
                    )}
            </Container>
        </>
    );
};
export default DadosPendentes;
